import type { CounterpartySnapshot } from '../counterparties/counterparties.dto.js';
import type {
  HoldingUnitCapabilities,
  InventoryTrackingMode,
} from '../holding-units/holding-units.dto.js';
import type { RcScopeType } from '../shipments/shipments-types.js';
import type { UnitProfileTemplateKey } from '../unit-profiles/unit-profiles.dto.js';
import type {
  AttachmentAvailabilityStatus,
  InboundDraftRevalidationCode,
  InboundReceiptMode,
  InboundReceiptPostingContainerKind,
  InboundReceiptPostingIdentityMode,
  InboundReceiptPostingProductKind,
  InboundReceiptPostingUnitType,
  InboundReceiptSourceEntryMode,
  InboundReceiptSourceType,
  InboundReceiptStatus,
  InventoryPostingStatus,
  OfficialComparisonInputStatus,
  OfficialComparisonOutputStatus,
  OfficialComparisonReviewStatus,
  OfficialComparisonStatus,
  OfficialComparisonStructuralStatus,
  OfficialSettlementCaptureSource,
  PredictedSettlementPaymentBasis,
  PredictedSettlementPredictionStatus,
  PredictedSettlementToleranceStatus,
  ReceivingGranularity,
} from './inbound-receipts-types.js';

export interface ReceivingTicketRowInput {
  label?: string | null;
  bagNumber?: string | null;
  grossLb: number;
  tareLb: number;
  notes?: string | null;
}

export interface InboundReceiptSourceDocument {
  shippingReportNumber?: string | null;
  entryMode?: InboundReceiptSourceEntryMode | null;
  materialId: string;
  materialTypeLabel: string;
  shipperCertificationId: string;
  shipperNameSnapshot: string;
  shipperAddressSnapshot: string;
  receiverCertificationId: string;
  receiverNameSnapshot: string;
  receiverAddressSnapshot: string;
  reportFrom?: string | null;
  reportThrough?: string | null;
  shippedRedemptionWeightLb?: number | null;
  shippedRefundValueCents?: number | null;
  ratePerLb?: number | null;
  swLb?: number | null;
  scsCount?: number | null;
  sclCount?: number | null;
  spLb?: number | null;
  scrapRatePerLb?: number | null;
  redemptionWeightOverridden?: boolean | null;
  refundValueOverridden?: boolean | null;
}

export interface InboundReceiptDraftRevalidation {
  isValid: boolean;
  code?: InboundDraftRevalidationCode;
  message?: string;
}

export interface ReceivingTicketRowDto {
  id: string;
  sortOrder: number;
  label?: string | null;
  bagNumber?: string | null;
  grossLb?: number | null;
  tareLb?: number | null;
  netLb: number;
  notes?: string | null;
}

export interface ReceivingTicketDto {
  id: string;
  receiptId: string;
  ticketNumber: string;
  materialId: string;
  shipperCertificationId: string;
  receiverCertificationId: string;
  issuedAt: string;
  finalizedAt?: string | null;
  measurementMode: InboundReceiptMode;
  totalNetLb: number;
  vehiclePlate?: string | null;
  scaleLocation?: string | null;
  createdByUserId: string;
  finalizedByUserId?: string | null;
  rows: ReceivingTicketRowDto[];
}

export interface InboundReceiptAuditEntryDto {
  id: string;
  action: string;
  occurredAt: string;
  actorUserId?: string | null;
  notes?: string | null;
}

export interface InboundReceiptDisplayHeaderSummary {
  receiptId: string;
  receiptStatusCode: InboundReceiptStatus;
  receiptStatusLabel: string;
  sourceTypeCode: InboundReceiptSourceType;
  sourceTypeLabel: string;
  materialId: string;
  materialLabel: string;
  shipperName: string;
  receiverName: string;
  confirmedAt: string;
  receivingTicketNumber: string | null;
  transferReference: string | null;
}

export interface InboundReceiptDisplayCoverStatus {
  statusCode: 'OFFICIAL_PENDING' | 'READY_FOR_COVER' | 'BLOCKED' | 'CONSUMED';
  label: 'Official pending' | 'Ready for cover' | 'Blocked' | 'Consumed';
  helpText: string;
}

export interface InboundReceiptDisplaySourceSummary {
  entryMode: InboundReceiptSourceEntryMode | null;
  reportFrom: string | null;
  reportThrough: string | null;
  shippedRedemptionWeightLb: number | null;
  shippedRefundValueCents: number | null;
  ratePerLb: number | null;
  swLb: number | null;
  scsCount: number | null;
  sclCount: number | null;
  spLb: number | null;
  scrapRatePerLb: number | null;
  redemptionWeightOverridden: boolean;
  refundValueOverridden: boolean;
}

export interface InboundReceiptDisplayLocalPaymentsSummary {
  visible: boolean;
  informationalOnly: boolean;
  localSourceRefundValueCents: number | null;
  localRatePerLb: number | null;
  scrapWeightLb: number | null;
  scrapValueCents: number | null;
  helpText: string | null;
}

export interface InboundReceiptDisplayReceivingSummary {
  receivedAt: string;
  transferReference: string | null;
  ticketNumber: string | null;
  vehiclePlate: string | null;
  scaleLocation: string | null;
  measurementMode: InboundReceiptMode | null;
  rowCount: number;
  totalReceivedWeightLb: number;
  rows: ReceivingTicketRowDto[];
  notes: string | null;
}

export interface InboundReceiptDisplayOfficialSettlementSummary {
  hasOfficialSettlement: boolean;
  captureSource: OfficialSettlementCaptureSource | null;
  shippingReportNumber: string | null;
  receivedDate: string | null;
  weightTicketNumber: string | null;
  receivedWeightLb: number | null;
  paymentWeightLb: number | null;
  shrinkagePercent: number | null;
  reductionFactor: number | null;
  refundValueCents: number | null;
  processingFeeCents: number | null;
  administrativeFeeCents: number | null;
  totalPaymentDueCents: number | null;
  onHold: boolean | null;
  capturedAt: string | null;
  importedAt: string | null;
  activeOfficialRevisionId: string | null;
  activeOfficialSnapshotId: string | null;
}

export interface InboundReceiptDisplayDifferenceRow {
  label: string;
  appValue: string | number | boolean | null;
  officialValue: string | number | boolean | null;
}

export interface InboundReceiptDisplayComparisonSummary {
  structuralStatus: OfficialComparisonStructuralStatus;
  inputStatus: OfficialComparisonInputStatus;
  outputStatus: OfficialComparisonOutputStatus;
  comparisonStatus: OfficialComparisonStatus;
  reviewStatus: OfficialComparisonReviewStatus;
  message: string;
  showDifferenceTable: boolean;
  showReviewAction: boolean;
  differenceRows: InboundReceiptDisplayDifferenceRow[];
}

export const inboundReceiptReceiverCompletionStatusValues = ['READY', 'INCOMPLETE'] as const;
export type InboundReceiptReceiverCompletionStatus =
  (typeof inboundReceiptReceiverCompletionStatusValues)[number];

export const inboundReceiptReceiverCompletionProvenanceKindValues = [
  'APP_ENTERED',
  'MANUAL_EVIDENCE',
  'IMPORTED_EVIDENCE',
] as const;
export type InboundReceiptReceiverCompletionProvenanceKind =
  (typeof inboundReceiptReceiverCompletionProvenanceKindValues)[number];

export interface InboundReceiptReceiverCompletionProvenance {
  kind: InboundReceiptReceiverCompletionProvenanceKind;
  sourceLabel: string;
  evidenceLabel: string;
  notDoriisUploadFile: true;
  notDoriisSubmissionProofUnlessLinkedEvidence: true;
  notes: string[];
}

export interface InboundReceiptReceiverCompletionEvidenceSummary {
  receivingTicketNumber: string | null;
  weightTicketNumber: string | null;
  doriisEvidenceLabel: string;
  linkedSourceReportEvidenceLabel: string | null;
  evidenceNotes: string[];
}

export interface InboundReceiptDisplayReceiverCompletionSummary {
  documentTitle: 'Receiver Completion';
  status: InboundReceiptReceiverCompletionStatus;
  completenessWarnings: string[];
  provenance: InboundReceiptReceiverCompletionProvenance;
  evidenceSummary: InboundReceiptReceiverCompletionEvidenceSummary;
}

export interface InboundReceiptDisplayInventoryPostingSummary {
  visible: boolean;
  canPostInventory: boolean;
  trackingMode?: InventoryTrackingMode | null;
  postingStatus: InventoryPostingStatus;
  inventoryStateLabel?: string;
  helpText?: string;
  actionLabel?: string | null;
  autoCreated?: boolean;
  postedWeightLb: number | null;
  postedAt: string | null;
  postedByUserId: string | null;
  createdHoldingUnitId: string | null;
  reversedAt: string | null;
  reversedByUserId: string | null;
}

export interface InboundReceiptDisplayPrintActions {
  canPrintReceivingTicket: boolean;
  canPrintReceiptSummary: boolean;
  canPrintReceiverCompletion: boolean;
  canPrintOfficialSettlementSummary?: boolean;
}

export interface InboundReceiptInventoryPostingUnitDto {
  id: string;
  sortOrder: number;
  receivingTicketRowId?: string | null;
  materialId: string;
  postedWeightLb: number;
  unitProfileId?: string | null;
  unitProfileRevision?: number | null;
  unitProfileDisplayName?: string | null;
  unitProfileTemplateKey?: UnitProfileTemplateKey | null;
  capabilityPolicy?: HoldingUnitCapabilities | null;
  unitType: InboundReceiptPostingUnitType;
  identityMode: InboundReceiptPostingIdentityMode;
  containerKind: InboundReceiptPostingContainerKind;
  productKind: InboundReceiptPostingProductKind;
  fixedIdAssetId?: string | null;
  fixedIdAssetPhysicalId?: string | null;
  fixedIdAssetExpectedVersion?: number | null;
  createdHoldingUnitId: string;
  label?: string | null;
  bagNumber?: string | null;
  notes?: string | null;
}

export interface InboundReceiptPostingUnitRegistrationInput {
  sortOrder: number;
  fixedIdAssetId?: string | null;
  expectedFixedIdAssetVersion?: number | null;
}

export interface InboundReceiptPostingUnitRegistrationDto
  extends InboundReceiptPostingUnitRegistrationInput {
  fixedIdAssetPhysicalId?: string | null;
}

export interface InboundReceiptDisplayDto {
  headerSummary: InboundReceiptDisplayHeaderSummary;
  coverStatus: InboundReceiptDisplayCoverStatus;
  sourceSummary: InboundReceiptDisplaySourceSummary;
  localPaymentsSummary: InboundReceiptDisplayLocalPaymentsSummary;
  receivingSummary: InboundReceiptDisplayReceivingSummary;
  officialSettlementSummary: InboundReceiptDisplayOfficialSettlementSummary;
  comparisonSummary: InboundReceiptDisplayComparisonSummary;
  receiverCompletionSummary: InboundReceiptDisplayReceiverCompletionSummary;
  inventoryPostingSummary: InboundReceiptDisplayInventoryPostingSummary;
  printActions: InboundReceiptDisplayPrintActions;
}

export interface InboundReceiptDraftReceiving {
  ticketId: string;
  ticketNumber: string;
  receivedAt: string;
  vehiclePlate?: string | null;
  scaleLocation?: string | null;
  totalReceivedWeightLb: number;
  measurementMode: InboundReceiptMode;
  rows: ReceivingTicketRowInput[];
  notes?: string | null;
}

export interface InboundReceiptDetailDto {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  receiverRcScopeType: RcScopeType;
  receiverRcScopeId: string;
  status: InboundReceiptStatus;
  sourceType: InboundReceiptSourceType;
  receivingGranularity: ReceivingGranularity;
  unitProfileId?: string | null;
  unitProfileRevision?: number | null;
  unitProfileDisplayName?: string | null;
  unitProfileTemplateKey?: UnitProfileTemplateKey | null;
  capabilityPolicy?: HoldingUnitCapabilities | null;
  unitType: InboundReceiptPostingUnitType;
  identityMode: InboundReceiptPostingIdentityMode;
  containerKind: InboundReceiptPostingContainerKind;
  productKind: InboundReceiptPostingProductKind;
  postingUnitRegistrations?: InboundReceiptPostingUnitRegistrationDto[];
  sourceShipmentId?: string | null;
  sourceDocument: InboundReceiptSourceDocument;
  operational: {
    measurementMode: InboundReceiptMode;
    receivedAt: string;
    receivingTicketId?: string | null;
    internalTransferReference?: string | null;
    receivedWeightLb: number;
    unitRows: ReceivingTicketRowDto[];
    notes?: string | null;
  };
  doriisInput: {
    receivedDate: string;
    weightTicketNumber: string;
    receivedWeightLb: number;
    shrinkagePercent?: number | null;
  };
  predictedSettlement: {
    formulaVersion: string;
    predictionStatus: PredictedSettlementPredictionStatus;
    reductionFactor?: number | null;
    toleranceStatus: PredictedSettlementToleranceStatus;
    paymentBasis: PredictedSettlementPaymentBasis;
    paymentWeightLb?: number | null;
    notes?: string | null;
  };
  officialSettlement: {
    captureSource?: OfficialSettlementCaptureSource | null;
    shippingReportNumber?: string | null;
    shippingReportNumberKey?: string | null;
    receivedDate?: string | null;
    weightTicketNumber?: string | null;
    receivedWeightLb?: number | null;
    shrinkagePercent?: number | null;
    reductionFactor?: number | null;
    paymentWeightLb?: number | null;
    refundValueCents?: number | null;
    processingFeeCents?: number | null;
    administrativeFeeCents?: number | null;
    totalPaymentDueCents?: number | null;
    onHold?: boolean | null;
    capturedAt?: string | null;
    capturedByUserId?: string | null;
    importedAt?: string | null;
    activeOfficialRevisionId?: string | null;
    activeOfficialSnapshotId?: string | null;
  };
  officialComparison: {
    structuralStatus: OfficialComparisonStructuralStatus;
    inputStatus: OfficialComparisonInputStatus;
    outputStatus: OfficialComparisonOutputStatus;
    comparisonStatus: OfficialComparisonStatus;
    reviewStatus: OfficialComparisonReviewStatus;
    comparisonNotes?: string | null;
    reviewedAt?: string | null;
    reviewedByUserId?: string | null;
  };
  inventoryPosting: {
    postingStatus: InventoryPostingStatus;
    postedWeightLb?: number | null;
    postedAt?: string | null;
    postedByUserId?: string | null;
    createdHoldingUnitId?: string | null;
    createdHoldingUnitIds?: string[];
    postingUnits?: InboundReceiptInventoryPostingUnitDto[];
    reversedAt?: string | null;
    reversedByUserId?: string | null;
  };
  attachmentConsumption: {
    availabilityStatus: AttachmentAvailabilityStatus;
    consumedByCoverShipmentId?: string | null;
    consumedAt?: string | null;
  };
  audit: {
    entries: InboundReceiptAuditEntryDto[];
  };
  createdAt: string;
  createdByUserId: string;
  confirmedAt: string;
  confirmedByUserId: string;
  canceledAt?: string | null;
  canceledByUserId?: string | null;
  display?: InboundReceiptDisplayDto;
}

export interface InboundReceiptDraft {
  draftId: string;
  companyId: string;
  stationId: string;
  sourceType: InboundReceiptSourceType;
  mode: InboundReceiptMode;
  receivingGranularity: ReceivingGranularity;
  unitProfileId?: string | null;
  unitProfileRevision?: number | null;
  unitProfileDisplayName?: string | null;
  unitProfileTemplateKey?: UnitProfileTemplateKey | null;
  capabilityPolicy?: HoldingUnitCapabilities | null;
  unitType: InboundReceiptPostingUnitType;
  identityMode: InboundReceiptPostingIdentityMode;
  containerKind: InboundReceiptPostingContainerKind;
  productKind: InboundReceiptPostingProductKind;
  postingUnitRegistrations?: InboundReceiptPostingUnitRegistrationDto[];
  materialId: string;
  sourceShipmentCompanyId: string | null;
  sourceShipmentId: string | null;
  shipper: CounterpartySnapshot;
  receiver: CounterpartySnapshot;
  sourceDocument: InboundReceiptSourceDocument;
  transferReference: string | null;
  receiving: InboundReceiptDraftReceiving;
  createdAt: string;
  updatedAt: string;
  revalidation: InboundReceiptDraftRevalidation;
}

export interface InboundReceiptAttachmentSummary {
  receiptId: string;
  materialId: string;
  receiverRcScopeType: RcScopeType;
  receiverRcScopeId: string;
  sourceType: InboundReceiptSourceType;
  shipper: CounterpartySnapshot;
  receiver: CounterpartySnapshot;
  shippingReportNumber: string;
  receivedWeightLb: number;
  paymentWeightLb: number | null;
  refundValueCents: number | null;
  receivedAt: string;
  internalTransferReference: string | null;
  availabilityStatus: AttachmentAvailabilityStatus;
  comparisonStatus: OfficialComparisonStatus;
  reviewStatus: OfficialComparisonReviewStatus;
  confirmedAt: string;
  consumedByCoverShipmentId: string | null;
}
